import { BlogAuthor } from "@/.storyblok/types/319751/storyblok-components";
import Image from "next/image";
import Link from "next/link";
import { SbBlokData, storyblokEditable } from "@storyblok/react";

const BlogAuthorBulletproof = ({ content, authorPosts }: { content: BlogAuthor & SbBlokData; authorPosts?: any[] | null }) => {
  const { title, bio, image, linkedin, cta, name } = content;

  const getPostImage = (post: any) => {
    const blogDetail = post.content?.blog_details?.[0];
    if (blogDetail?.thumb?.filename) {
      return blogDetail.thumb.filename;
    }
    return post.content?.metatags?.og_image;
  };

  const getPostTitle = (post: any) => {
    if (post.name) {
      return post.name;
    }
    if (post.content?.metatags?.og_title) {
      return post.content.metatags.og_title;
    }
    return post.content?.metatags?.title;
  };


  const AuthorBio = () => (
    <div className="bg-light py-4" id="author-bio">
      <div className="container">
        <div className="two-thirds-centered">
          <div className="row">
            <div className="col-24 col-md-8 text-center mb-4 mb-md-0">
              {image?.filename && (
                <Image
                  src={image.filename}
                  alt={`${name} Headshot`}
                  className="blog-author-img rounded-circle"
                  width={180}
                  height={180}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
            </div>
            <div className="col-24 col-md-16 blog-author-meta">
              <h3 className="text-muted text-uppercase blog-author-heading mb-2">
                Meet the author
              </h3>
              <h4 className="h3">
                {name} <span className="blog-author-title">{title}</span>
              </h4>
              {bio && <p>{bio}</p>}
              {linkedin?.url && (
                <ul className="list-unstyled mb-0">
                  <li>
                    <i className="fab fa-fw fa-linkedin me-2 text-secondary" aria-hidden="true" />
                    <a
                      className="text-secondary"
                      href={linkedin.url}
                      target="_blank"
                      rel="noopener"
                    >
                      Follow {name} on LinkedIn
                    </a>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const AuthorCTA = () => (
    cta?.[0] && (
      <div className="border-top row justify-content-center p-4">
        <div className="carousel-testimonial col-lg-20 col-xl-18 col-xxl-16 text-center">
          <h2 className="text-secondary">{cta[0].title}</h2>
          <blockquote className="unstyled text-center quote-red mb-3 mb-md-4">
            {cta[0].body}
          </blockquote>
          {cta[0].btn_link?.url && (
            <Link
              href={cta[0].btn_link.url.startsWith('http')
                ? { pathname: cta[0].btn_link.url as string }
                : { pathname: cta[0].btn_link.url.startsWith('/')
                    ? cta[0].btn_link.url
                    : `/${cta[0].btn_link.url}`
                }
              }
              className="btn btn-secondary btn-lg mt-2 mt-lg-3"
            >
              {cta[0].btn_text}
            </Link>
          )}
        </div>
      </div>
    )
  );

  const AuthorPosts = () => (
    authorPosts && authorPosts.length > 0 && (
      <div className="py-4 py-md-5">
        <div className="container blog-latest">
          <h2 className="text-center mb-5">Read more by {name}</h2>
          <div className="blog-grid">
            {authorPosts.map((post: any) => {
              const content = post.content;
              if (!content || !Array.isArray(content.blog_details) || content.blog_details.length === 0) {
                return null;
              }

              const blogDetail = content.blog_details[0];
              const topic = blogDetail.topics || "";

              return (
                <Link
                  href={`/blog/${post.slug}`}
                  key={post.uuid}
                  className="blog-preview blog-result-0 card card-lighter scroll-trigger text-dark"
                  data-filter={post.name}
                  data-topic={topic.toLowerCase().replace(/\s/g, '-')}
                >
                  <div className="blog-preview-image position-relative overflow-hidden">
                    {getPostImage(post) && (
                      <Image
                        src={getPostImage(post)}
                        alt={typeof title === 'string' ? title : ''}
                        fill
                        className="object-fit-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                  </div>
                  <div className="blog-preview-content p-3 p-lg-4">
                    <h3 className="title blog-preview-text mb-3">
                      {getPostTitle(post)}
                    </h3>
                    <p className="blog-preview-note mb-3">
                      {image?.filename && (
                        <Image
                          src={image.filename}
                          alt={getPostTitle(post)}
                          className="rounded-circle me-1"
                          width={30}
                          height={30}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      )}
                      {name}
                    </p>
                    <p className="blog-preview-link mb-0">
                      <span className="d-block btn btn-primary">Read More</span>
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    )
  );

  return (
    <div {...storyblokEditable(content)} className="bg-light py-4 py-md-5">
      <div className="container blog-featured">
        <div className="blog-preview-featured card card-lighter flex-column">
          <AuthorBio />
          <AuthorCTA />
        </div>
      </div>
      <AuthorPosts />
    </div>
  );
};

export default BlogAuthorBulletproof;